import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import api from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const LoginPage = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const me = await api.me();
        if (me?.is_admin) navigate("/admin", { replace: true });
      } catch {
        /* not authed — stay on login */
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await api.login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Sign-in failed. Check your credentials.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col" data-testid="login-page">
      <header className="border-b border-ink-line bg-paper">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="login-back-home"><Logo size="sm" /></Link>
          <Link to="/" className="text-sm text-ink-soft hover:text-ink" data-testid="login-back-home-text">← Back to site</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md">
          <div className="text-center">
            <div className="overline mb-6">Editorial Console</div>
            <h1 className="font-display font-normal text-ink text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
              Sign in to <span className="italic">manage</span> the publication.
            </h1>
            <p className="mt-4 text-ink-soft text-sm md:text-base leading-relaxed">
              Restricted to authorised editors of World Visionary Times.
            </p>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="login-email" className="overline overline-muted block mb-2">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-paper border border-ink-line rounded text-ink text-sm outline-none focus:border-ink"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="overline overline-muted block mb-2">Password</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  className="w-full pl-4 pr-11 py-3 bg-paper border border-ink-line rounded text-ink text-sm outline-none focus:border-ink"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-toggle-visibility"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-ink-soft hover:text-ink"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-[13px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || checking}
              data-testid="login-submit-btn"
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-ink text-paper font-medium text-sm tracking-wide rounded-full hover:bg-gold-dark transition-colors disabled:opacity-40"
            >
              {submitting ? "Signing in…" : (<>Sign In <ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-10 flex items-center justify-center gap-2 text-xs text-ink-muted">
            <ShieldCheck size={13} strokeWidth={1.4} />
            <span>Secured with encrypted credentials</span>
          </div>
        </div>
      </main>

      <footer className="border-t border-ink-line py-6 text-center text-xs text-ink-muted">
        © {new Date().getFullYear()} World Visionary Times · Editorial Console
      </footer>
    </div>
  );
};

export default LoginPage;
